// src/lib/analytics.ts
//
// GA4 helpers used by src/app/providers.tsx.
//
// Healthcare privacy constraints:
// - No collection of any kind on private routes (patient portal, CMS studio).
//   The official `ga-disable-<id>` flag is used as a kill switch so Enhanced
//   Measurement events (scroll, click, engagement, downloads, video) stop
//   too, not just our manual page_view.
// - The raw query string is never sent to GA4: it can encode health-related
//   selections (e.g. /adhd-autism-assessment-tool?tool=adhd). page_path and
//   page_location are always query-free. The only values ever read from
//   location.search are three strictly validated, low-cardinality campaign
//   tokens (utm_source / utm_medium / utm_campaign), read exactly once at
//   initial GA configuration for campaign attribution; every other query
//   parameter is discarded unread.

export const PRIVATE_ROUTE_PREFIXES = [
  '/patient', // patient portal (includes /patient/[patient_id])
  '/studio', // Sanity CMS admin
  '/nav-demo', // internal component playground
] as const

type AnalyticsState = {
  initialized: boolean
  lastTrackedPath: string | null
  firedThisPageView: Set<string>
}

const state: AnalyticsState = {
  initialized: false,
  lastTrackedPath: null,
  firedThisPageView: new Set(),
}

export function resetAnalyticsStateForTests(): void {
  state.initialized = false
  state.lastTrackedPath = null
  state.firedThisPageView.clear()
}

/**
 * Single source of truth for whether analytics may run. The provider and
 * every custom event share this rule — analytics is opt-in per environment
 * and never runs outside production builds.
 */
export function isAnalyticsEnabled(): boolean {
  return (
    process.env.NEXT_PUBLIC_ENABLE_ANALYTICS === 'true' &&
    Boolean(process.env.NEXT_PUBLIC_GA4_ID) &&
    process.env.NODE_ENV === 'production'
  )
}

/** Strip query string and hash fragment; never returns an empty path. */
export function sanitizeAnalyticsPath(path: string): string {
  const bare = path.split(/[?#]/, 1)[0]
  return bare || '/'
}

/** Absolute page_location built only from origin + sanitized path. */
export function sanitizeAnalyticsLocation(
  origin: string,
  path: string,
): string {
  return `${origin}${sanitizeAnalyticsPath(path)}`
}

export type SafeCampaignParams = {
  campaign_source?: string
  campaign_medium?: string
  campaign_name?: string
}

const CAMPAIGN_PARAM_MAP: ReadonlyArray<
  [utmKey: string, campaignKey: keyof SafeCampaignParams]
> = [
  ['utm_source', 'campaign_source'],
  ['utm_medium', 'campaign_medium'],
  ['utm_campaign', 'campaign_name'],
]

// Low-cardinality campaign tokens only: alphanumeric start, then letters,
// digits, dot, underscore, tilde or hyphen, 100 chars max. This excludes
// spaces, "@" (emails), slashes (URLs/paths), "+", "(", ")" and every other
// character that could carry free-form or contact data.
const CAMPAIGN_TOKEN_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._~-]{0,99}$/

/**
 * Reads ONLY utm_source / utm_medium / utm_campaign from a query string and
 * maps them to GA4's explicit campaign configuration fields. Values that are
 * not strict low-cardinality tokens (or that look like bare phone numbers)
 * are dropped silently — rejected values are never logged and the original
 * query string is never returned. All other parameters (utm_term,
 * utm_content, gclid, fbclid, tool, service, …) are ignored entirely.
 */
export function readSafeCampaignParams(search: string): SafeCampaignParams {
  const result: SafeCampaignParams = {}
  let params: URLSearchParams
  try {
    params = new URLSearchParams(search)
  } catch {
    return result
  }
  for (const [utmKey, campaignKey] of CAMPAIGN_PARAM_MAP) {
    const raw = params.get(utmKey)
    if (raw === null) continue
    const value = raw.trim()
    if (!CAMPAIGN_TOKEN_PATTERN.test(value)) continue
    // The token pattern already blocks "+", spaces and parentheses, but a
    // bare digit string could still be a phone number — reject anything
    // that is 7+ digits once separator characters are removed.
    if (/^\d{7,}$/.test(value.replace(/[._~-]/g, ''))) continue
    result[campaignKey] = value
  }
  return result
}

// Canonical 8-4-4-4-12 hex UUID, case-insensitive, regardless of version or
// variant nibble: future UUID versions and malformed nibbles are still opaque
// identifiers, never editorial content.
const UUID_PATH_SEGMENT_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export function isTrackablePath(pathname: string | null | undefined): boolean {
  if (!pathname) return false
  const path = sanitizeAnalyticsPath(pathname)
  if (
    PRIVATE_ROUTE_PREFIXES.some(
      (prefix) => path === prefix || path.startsWith(`${prefix}/`),
    )
  ) {
    return false
  }
  // No public route uses UUID-shaped segments (the only public dynamic route
  // is /blog/[slug], whose slugs are editorial word slugs), so a UUID in a
  // path is an opaque record identifier and must never reach analytics —
  // even if the private-prefix list misses the route that exposed it.
  return !path
    .split('/')
    .some((segment) => UUID_PATH_SEGMENT_PATTERN.test(segment))
}

/**
 * Official gtag opt-out flag: while true, gtag.js drops every hit for this
 * measurement ID, including Enhanced Measurement events. Exported ONLY for
 * the navigation guard (analytics-navigation-guard.ts), which must engage
 * the kill switch synchronously during unsafe history transitions; feature
 * code must never call this directly.
 */
export function setAnalyticsCollectionDisabled(
  gaId: string,
  disabled: boolean,
): void {
  ;(window as unknown as Record<string, unknown>)[`ga-disable-${gaId}`] =
    disabled
}

function ensureGtag(): Window['gtag'] {
  window.dataLayer = window.dataLayer || []
  if (!window.gtag) {
    window.gtag = function gtag() {
      // gtag.js requires the Arguments object itself, not a spread array.
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments)
    }
  }
  return window.gtag
}

/**
 * Route-change handler: enables/disables collection based on the route and
 * sends exactly one sanitized page_view per permitted navigation.
 *
 * This acknowledgement is also the ONLY path that lifts the quarantine the
 * navigation guard (analytics-navigation-guard.ts) engages during unsafe
 * history transitions: collection resumes exactly when React lands on a
 * trackable route — never on a timer and never from a navigation event.
 *
 * Commands are queued on the dataLayer via the gtag stub, so ordering is
 * preserved even when this runs before gtag.js has finished loading.
 */
export function handleRouteChange(gaId: string, pathname: string): void {
  const trackable = isTrackablePath(pathname)
  setAnalyticsCollectionDisabled(gaId, !trackable)
  if (!trackable) {
    // Forget the last tracked path so returning to a public page after a
    // private detour counts as a fresh navigation.
    state.lastTrackedPath = null
    state.firedThisPageView.clear()
    return
  }

  const gtag = ensureGtag()
  if (!state.initialized) {
    state.initialized = true
    // Campaign attribution: the ONLY read of location.search in the entire
    // analytics layer. Three strictly validated UTM tokens are passed through
    // GA4's explicit campaign fields, once, at initial configuration; the raw
    // query string is never sent and page_path/page_location stay query-free.
    // Later client-side navigations never reapply campaign fields.
    const campaign = readSafeCampaignParams(window.location.search)
    gtag('js', new Date())
    gtag('config', gaId, {
      // Page views are sent manually below — never by config.
      send_page_view: false,
      transport_url: `${window.location.origin}/explorer`,
      ...campaign,
    })
  }

  const path = sanitizeAnalyticsPath(pathname)
  if (state.lastTrackedPath === path) return // re-render, not a navigation
  state.lastTrackedPath = path
  // New page view: oncePerPageView custom events may fire again.
  state.firedThisPageView.clear()

  const pageLocation = sanitizeAnalyticsLocation(window.location.origin, path)
  // Pin sanitized URL fields for every subsequent event on this page so
  // Enhanced Measurement events cannot leak the real location.search.
  gtag('set', {
    page_path: path,
    page_location: pageLocation,
    page_title: document.title,
  })
  gtag('event', 'page_view', {
    page_path: path,
    page_location: pageLocation,
    page_title: document.title,
  })
}

/* ── Custom conversion / engagement events ──────────────────────────────
 *
 * Closed taxonomy: only these events with exactly these parameter values can
 * ever reach GA4. Feature components must call trackAnalyticsEvent — never
 * window.gtag — so every event inherits the enablement rule, private-route
 * blocking, the ga-disable kill switch, and the sanitized page context that
 * handleRouteChange pins via gtag('set').
 */

export type AnalyticsEventName =
  | 'generate_lead'
  | 'whatsapp_click'
  | 'phone_click'
  | 'directions_click'
  | 'email_click'
  | 'clinician_profile_view'
  | 'service_page_engagement'

export type AnalyticsEventParamMap = {
  generate_lead: { lead_type: 'appointment_booking' }
  whatsapp_click: { cta_location: 'booking_options' | 'footer' | 'visit_us' }
  phone_click: {
    cta_location: 'booking_options' | 'footer' | 'assessment_tool' | 'visit_us'
  }
  directions_click: {
    cta_location: 'footer' | 'surrogacy_hero' | 'clinician_profile' | 'visit_us'
  }
  email_click: { cta_location: 'footer' | 'visit_us' }
  clinician_profile_view: { content_type: 'clinician_profile' }
  service_page_engagement: { content_type: 'service_page' }
}

// Runtime mirror of the type map. Compile-time unions cannot protect against
// values smuggled through casts, so every key AND value is re-checked before
// anything is pushed to the dataLayer; a non-allowlisted key or value drops
// the whole event.
const ALLOWED_EVENT_PARAMS: Record<
  AnalyticsEventName,
  Record<string, readonly string[]>
> = {
  generate_lead: { lead_type: ['appointment_booking'] },
  whatsapp_click: { cta_location: ['booking_options', 'footer', 'visit_us'] },
  phone_click: {
    cta_location: ['booking_options', 'footer', 'assessment_tool', 'visit_us'],
  },
  directions_click: {
    cta_location: ['footer', 'surrogacy_hero', 'clinician_profile', 'visit_us'],
  },
  email_click: { cta_location: ['footer', 'visit_us'] },
  clinician_profile_view: { content_type: ['clinician_profile'] },
  service_page_engagement: { content_type: ['service_page'] },
}

function sanitizeEventParams(
  eventName: AnalyticsEventName,
  params: Record<string, unknown>,
): Record<string, string> | null {
  const allowed = ALLOWED_EVENT_PARAMS[eventName]
  if (!allowed) return null

  const safe: Record<string, string> = {}
  for (const [key, value] of Object.entries(params)) {
    const allowedValues = allowed[key]
    if (
      !allowedValues ||
      typeof value !== 'string' ||
      !allowedValues.includes(value)
    ) {
      return null
    }
    safe[key] = value
  }
  // Every allowlisted key is required — an event without its one safe
  // parameter is malformed and is dropped rather than sent partially.
  for (const key of Object.keys(allowed)) {
    if (!(key in safe)) return null
  }
  return safe
}

export function trackAnalyticsEvent<E extends AnalyticsEventName>(
  eventName: E,
  safeParams: AnalyticsEventParamMap[E],
  options?: { oncePerPageView?: boolean },
): void {
  if (typeof window === 'undefined') return
  if (!isAnalyticsEnabled()) return

  const gaId = process.env.NEXT_PUBLIC_GA4_ID
  if (!gaId) return

  // Custom events never read location.search / location.hash — pathname
  // only, defensively sanitized like everything else. (The single read of
  // location.search in this module is readSafeCampaignParams at initial GA
  // configuration, and even there only three validated tokens survive.)
  const path = sanitizeAnalyticsPath(window.location.pathname)
  if (!isTrackablePath(path)) return
  if (
    (window as unknown as Record<string, unknown>)[`ga-disable-${gaId}`] ===
    true
  ) {
    return
  }

  const params = sanitizeEventParams(eventName, safeParams)
  if (!params) return

  if (options?.oncePerPageView) {
    if (state.firedThisPageView.has(eventName)) return
    state.firedThisPageView.add(eventName)
  }

  // Queues on the dataLayer stub when gtag.js has not finished loading;
  // gtag.js drains the queue in order once ready.
  ensureGtag()('event', eventName, params)
}
